import React from 'react';
import { Alert, Button, IconButton, Snackbar } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import type {
  EdgeOnboardGoLiveState,
  EdgeOnboardReviewState,
  EdgeOnboardSlipState,
  EdgeOnboardSubmissionReviewState,
} from '../../api/config';
import { hexahqAccountUrl } from '../../utils/hexahq';
import './EdgeOnboard.css';

// Shared presentation pieces for the Edge Onboard admin pages. All markup
// assumes it renders inside <EoPage> (the .eo-scope wrapper that carries the
// scoped v3 design tokens from EdgeOnboard.css).

/** Material Icons ligature glyph (font is loaded globally in index.html). */
export const EoIcon: React.FC<{ name: string; className?: string }> = ({ name, className }) => (
  <span className={`ea-ico ${className ?? ''}`} aria-hidden="true">
    {name}
  </span>
);

export type EoTone = 'success' | 'warning' | 'error' | 'info' | 'default' | 'primary';

export const EoChip: React.FC<{
  tone: EoTone;
  filled?: boolean;
  small?: boolean;
  icon?: string;
  title?: string;
  children: React.ReactNode;
}> = ({ tone, filled, small, icon, title, children }) => (
  <span
    className={`ea-chip ${tone}${filled ? ' filled' : ''}${small ? ' small' : ''}`}
    title={title}
  >
    {icon && <EoIcon name={icon} />}
    {children}
  </span>
);

// Status vocabulary from the pattern reference: go-live/review states render
// filled when "hot" (In Progress / Complete), slip + submission states stay
// outlined. Icon + label always — never color alone.

export const GoLiveStateChip: React.FC<{ state: EdgeOnboardGoLiveState; small?: boolean }> = ({ state, small }) => {
  switch (state) {
    case 'In Progress':
      return <EoChip tone="info" filled small={small} icon="bolt">In Progress</EoChip>;
    case 'Complete':
      return <EoChip tone="success" filled small={small} icon="check_circle">Complete</EoChip>;
    default:
      return <EoChip tone="default" small={small} icon="pause_circle">Not Started</EoChip>;
  }
};

export const ReviewStateChip: React.FC<{ state: EdgeOnboardReviewState; small?: boolean }> = ({ state, small }) => {
  switch (state) {
    case 'Under Review':
      return <EoChip tone="warning" small={small} icon="rate_review">Under Review</EoChip>;
    case 'Complete':
      return <EoChip tone="success" small={small} icon="check_circle">Complete</EoChip>;
    default:
      return <EoChip tone="default" small={small} icon="remove">Not Started</EoChip>;
  }
};

export const SlipStateChip: React.FC<{ state: EdgeOnboardSlipState; small?: boolean }> = ({ state, small }) => {
  switch (state) {
    case 'Assigned':
      return <EoChip tone="success" small={small} icon="check_circle">Assigned</EoChip>;
    case 'Pending':
      return <EoChip tone="warning" small={small} icon="schedule">Pending</EoChip>;
    default:
      return <EoChip tone="default" small={small} icon="circle">Available</EoChip>;
  }
};

export const SubmissionReviewChip: React.FC<{ state: EdgeOnboardSubmissionReviewState; small?: boolean }> = ({
  state,
  small,
}) => {
  return state === 'Reviewed' ? (
    <EoChip tone="success" small={small} icon="check_circle">Reviewed</EoChip>
  ) : (
    <EoChip tone="warning" small={small} icon="schedule">Pending</EoChip>
  );
};

/**
 * "HQ #123" deep-linked into the HexaHQ account. HexaHQ is a different
 * application on its own domain, so this is the one link on these pages that
 * opens in a new tab — in-app navigation stays same-tab.
 *
 * Returns null when the location carries no HQ link; callers decide what the
 * absence looks like (an em dash on the index, nothing at all on the detail
 * header) rather than getting a link to /accounts/null.
 */
export const HexahqAccountLink: React.FC<{ accountId: number | null | undefined }> = ({ accountId }) => {
  if (accountId == null) return null;
  return (
    <a
      className="eo-hq-link"
      href={hexahqAccountUrl(accountId)}
      target="_blank"
      rel="noopener noreferrer"
      title={`Open account ${accountId} in HexaHQ`}
      // Rows are clickable elsewhere; don't let the account link double-fire them.
      onClick={e => e.stopPropagation()}
    >
      HQ #{accountId}
      <EoIcon name="open_in_new" className="eo-hq-link__ico" />
    </a>
  );
};

/** Page wrapper — applies the scoped design tokens. */
export const EoPage: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="eo-scope">{children}</div>
);

export const EoPageHeader: React.FC<{ title: string; sub: string; action?: React.ReactNode }> = ({
  title,
  sub,
  action,
}) => (
  <div className="eo-page-head">
    <div>
      <h1 className="eo-title">{title}</h1>
      <p className="eo-sub">{sub}</p>
    </div>
    {action}
  </div>
);

// ---- Client-side table sorting ----
// These tables load their whole collection and filter in memory (the universe is
// a few hundred rows), so sorting stays on the client too — no new API params.

export type EoSortDirection = 'asc' | 'desc';

export interface EoSortState<K extends string> {
  key: K;
  dir: EoSortDirection;
}

/** First click on a column sorts ascending; clicking the active column flips it. */
export function nextEoSort<K extends string>(current: EoSortState<K> | null, key: K): EoSortState<K> {
  if (current && current.key === key) {
    return { key, dir: current.dir === 'asc' ? 'desc' : 'asc' };
  }
  return { key, dir: 'asc' };
}

/**
 * Comparator for one column's extracted values. Null/undefined always sorts to
 * the END, in both directions — a row with no customer user or no counts stays
 * out of the way instead of leading the table when you flip direction.
 */
export function compareEoValues(
  a: string | number | null | undefined,
  b: string | number | null | undefined,
  dir: EoSortDirection,
): number {
  const aMissing = a === null || a === undefined || a === '';
  const bMissing = b === null || b === undefined || b === '';
  if (aMissing && bMissing) return 0;
  if (aMissing) return 1;
  if (bMissing) return -1;

  const sign = dir === 'asc' ? 1 : -1;
  if (typeof a === 'number' && typeof b === 'number') return (a - b) * sign;
  return String(a).localeCompare(String(b)) * sign;
}

interface EoSortHeaderProps<K extends string> {
  label: string;
  sortKey: K;
  sort: EoSortState<K> | null;
  onSort: (key: K) => void;
  align?: 'left' | 'center';
  /** Tooltip on the header cell (e.g. what an abbreviated column counts). */
  title?: string;
}

/**
 * Clickable column header. The direction arrow renders only on the active
 * column, and `aria-sort` carries the same state for screen readers and tests.
 */
export function EoSortHeader<K extends string>({
  label,
  sortKey,
  sort,
  onSort,
  align,
  title,
}: EoSortHeaderProps<K>) {
  const active = sort?.key === sortKey;
  return (
    <th
      style={align === 'center' ? { textAlign: 'center' } : undefined}
      aria-sort={active ? (sort?.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
      title={title}
    >
      <button
        type="button"
        className={`eo-sort${active ? ' active' : ''}`}
        aria-label={`Sort by ${label}`}
        onClick={() => onSort(sortKey)}
      >
        {label}
        <EoIcon
          name={active ? (sort?.dir === 'asc' ? 'arrow_upward' : 'arrow_downward') : 'unfold_more'}
          className="eo-sort__ico"
        />
      </button>
    </th>
  );
}

export const EoEmpty: React.FC<{ icon: string; message: string }> = ({ icon, message }) => (
  <div className="eo-empty">
    <EoIcon name={icon} />
    <div className="msg">{message}</div>
  </div>
);

// "May 2, 2026" — the design's date style for table cells.
export function formatEoDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

// "May 28, 2026 · 9:14 AM" — detail-page header style.
export function formatEoDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const date = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  return `${date} · ${time}`;
}

// "2 min ago" — slip pending_since style.
export function formatEoTimeAgo(iso: string | null | undefined): string {
  if (!iso) return '—';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '—';
  const seconds = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hr${hours === 1 ? '' : 's'} ago`;
  const days = Math.floor(hours / 24);
  return `${days} day${days === 1 ? '' : 's'} ago`;
}

// Snackbar state hook — same local-state pattern the rest of tags-admin uses,
// extracted so six pages don't each re-declare it.
export interface EoSnackbarAction {
  label: string;
  onClick: () => void;
}

export interface EoSnackbarState {
  message: string;
  severity: 'success' | 'error' | 'info';
  /** Optional next-step button — connective tissue between flow pages. */
  action?: EoSnackbarAction;
}

export function useEoSnackbar() {
  const [snackbar, setSnackbar] = React.useState<EoSnackbarState | null>(null);
  const showSuccess = React.useCallback(
    (message: string, action?: EoSnackbarAction) => setSnackbar({ message, severity: 'success', action }),
    [],
  );
  const showError = React.useCallback((message: string) => setSnackbar({ message, severity: 'error' }), []);
  const showInfo = React.useCallback((message: string) => setSnackbar({ message, severity: 'info' }), []);

  const snackbarElement = (
    <Snackbar
      open={Boolean(snackbar)}
      // Give action snackbars longer — the user is reading a choice.
      autoHideDuration={snackbar?.action ? 10000 : 5000}
      onClose={() => setSnackbar(null)}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
    >
      <Alert
        severity={snackbar?.severity ?? 'info'}
        onClose={() => setSnackbar(null)}
        sx={{ width: '100%', alignItems: 'center' }}
        action={
          snackbar?.action ? (
            // MUI hides the onClose X whenever `action` is set — include our
            // own dismiss so action snackbars aren't timeout-only.
            <>
              <Button
                color="inherit"
                size="small"
                sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}
                onClick={() => {
                  snackbar.action?.onClick();
                  setSnackbar(null);
                }}
              >
                {snackbar.action.label}
              </Button>
              <IconButton
                size="small"
                color="inherit"
                aria-label="Dismiss"
                onClick={() => setSnackbar(null)}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </>
          ) : undefined
        }
      >
        {snackbar?.message}
      </Alert>
    </Snackbar>
  );

  return { showSuccess, showError, showInfo, snackbarElement };
}
